import React, { useEffect } from "react";
import { currentLang } from "../../../utility/index";
import { ParsedErrorInfo } from "../utils/errorHelper";

export interface ErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  errorInfo?: ParsedErrorInfo | null;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  errorInfo,
}) => {
  const isArabic = currentLang() === "Arabic";

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const displayTitle =
    title ||
    errorInfo?.title ||
    (isArabic ? "تنبيه" : "Error Occurred");

  const displayMessage =
    message ||
    errorInfo?.message ||
    (isArabic ? "حدث خطأ غير متوقع." : "An unexpected error occurred.");

  const sapError = errorInfo?.sapError;
  const closeButtonText = isArabic ? "إغلاق" : "Close";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-7 flex flex-col border border-gray-100 my-auto transform transition-all duration-200"
        dir={isArabic ? "rtl" : "ltr"}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Icon Button */}
        <button
          onClick={onClose}
          type="button"
          aria-label="Close"
          className={`absolute top-4 ${
            isArabic ? "left-4" : "right-4"
          } text-gray-400 hover:text-gray-600 rounded-lg p-1.5 hover:bg-gray-100 transition-colors focus:outline-none`}
        >
          <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* Header with Icon and Title */}
        <div className="flex items-start gap-3.5 mb-4 pr-6 rtl:pr-0 rtl:pl-6">
          <div className="flex-shrink-0 w-11 h-11 rounded-full bg-red-100 text-red-600 flex items-center justify-center shadow-inner">
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-snug">
              {displayTitle}
            </h3>
          </div>
        </div>

        {/* Body of Error Popup */}
        <div className="mb-5">
          {sapError ? (
            <div className="bg-red-50/70 border border-red-200/80 rounded-xl p-3.5 text-xs text-red-900">
              <div className="font-semibold text-red-800 mb-1.5 flex items-center gap-1.5">
                <svg
                  className="w-4 h-4 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  {isArabic ? "تفاصيل استجابة SAP:" : "SAP Response Details:"}
                </span>
              </div>
              <div className="font-mono break-words leading-relaxed text-red-950/90 bg-white/80 rounded-lg p-2.5 border border-red-100 text-xs sm:text-sm">
                {sapError}
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-700 leading-relaxed bg-gray-50 border border-gray-200 rounded-xl p-3.5 max-h-60 overflow-y-auto">
              {displayMessage}
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="flex justify-end pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto min-w-[100px] px-5 py-2 text-sm font-medium text-white bg-[#1dbda1] hover:bg-[#17a488] active:bg-[#138e76] rounded-lg shadow-sm transition-colors focus:outline-none"
          >
            {closeButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorModal;
